import { getListContacts } from '@/modules/lists/services/lists.service';
import { getSegmentContacts } from '@/modules/segments/services/segments.service';
import {
  getCampaignAudienceEstimate,
  setCampaignAudienceRest,
} from '../services/campaigns-rest.service';
import type { AudienceSkipCounts } from '../types/campaigns-rest.types';

export type AudienceKpiCounts = {
  audience: number;
  sendable: number;
  skipped: number;
  /** Per-reason skip counts, only when the server estimate is available. */
  skips?: AudienceSkipCounts;
};

export type FetchAudienceKpiCountsInput = {
  /** When set, bind audience then use server estimate (unique contacts). */
  campaignId?: string;
  includeListIds: string[];
  includeSegmentIds: string[];
  excludeListIds?: string[];
  excludeSegmentIds?: string[];
};

const uniqueIds = (ids: string[]): string[] =>
  Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)));

const sumSkips = (skips: AudienceSkipCounts): number =>
  skips.unsubscribed +
  skips.pending +
  skips.suppressed +
  skips.noAddress +
  skips.invalidEmail +
  skips.noSubscriptionStatus;

const contactKey = (id: string | null | undefined, email: string | null | undefined): string | null => {
  const trimmedEmail = email?.trim().toLowerCase();
  if (trimmedEmail) return `email:${trimmedEmail}`;
  const trimmedId = id?.trim();
  if (trimmedId) return `id:${trimmedId}`;
  return null;
};

const PAGE_SIZE = 100;
/** Safety cap so KPI never walks unbounded membership pages. */
const MAX_PAGES_PER_SOURCE = 20;

const collectListContactKeys = async (
  listId: string,
  filter?: { emailStatus: string[] }
): Promise<Set<string>> => {
  const keys = new Set<string>();
  for (let pageNo = 1; pageNo <= MAX_PAGES_PER_SOURCE; pageNo += 1) {
    const page = await getListContacts({
      listId,
      pageNo,
      pageSize: PAGE_SIZE,
      filter,
    });
    for (const contact of page.items) {
      const key = contactKey(contact.ItemId, contact.Email);
      if (key) keys.add(key);
    }
    if (!page.hasNextPage || page.items.length === 0) break;
  }
  return keys;
};

const collectSegmentContactKeys = async (
  segmentId: string,
  sendableOnly: boolean
): Promise<Set<string>> => {
  const keys = new Set<string>();
  let after: string | undefined;
  for (let page = 0; page < MAX_PAGES_PER_SOURCE; page += 1) {
    const result = await getSegmentContacts(segmentId, { limit: PAGE_SIZE, after });
    for (const contact of result.items) {
      if (sendableOnly) {
        const status = contact.emailSubscriptionStatus?.trim().toLowerCase();
        if (status !== 'subscribed') continue;
      }
      const key = contactKey(contact.itemId, contact.email);
      if (key) keys.add(key);
    }
    if (!result.pageInfo.hasNext || !result.pageInfo.nextAfter) break;
    after = result.pageInfo.nextAfter;
  }
  return keys;
};

const fetchUniqueMembershipCounts = async (
  listIds: string[],
  segmentIds: string[]
): Promise<AudienceKpiCounts> => {
  const [audienceListSets, sendableListSets, audienceSegmentSets, sendableSegmentSets] =
    await Promise.all([
      Promise.all(listIds.map((id) => collectListContactKeys(id))),
      Promise.all(listIds.map((id) => collectListContactKeys(id, { emailStatus: ['subscribed'] }))),
      Promise.all(segmentIds.map((id) => collectSegmentContactKeys(id, false))),
      Promise.all(segmentIds.map((id) => collectSegmentContactKeys(id, true))),
    ]);

  const audienceKeys = new Set<string>();
  for (const set of [...audienceListSets, ...audienceSegmentSets]) {
    for (const key of set) audienceKeys.add(key);
  }

  const sendableKeys = new Set<string>();
  for (const set of [...sendableListSets, ...sendableSegmentSets]) {
    for (const key of set) sendableKeys.add(key);
  }

  const audience = audienceKeys.size;
  const sendable = sendableKeys.size;
  return { audience, sendable, skipped: Math.max(0, audience - sendable) };
};

/**
 * Audience KPIs for the wizard step.
 * Prefer campaign estimate (`netCount` = distinct contacts) when a campaign id is available.
 */
export const fetchAudienceKpiCounts = async (
  input: FetchAudienceKpiCountsInput
): Promise<AudienceKpiCounts> => {
  const listIds = uniqueIds(input.includeListIds);
  const segmentIds = uniqueIds(input.includeSegmentIds);

  if (listIds.length === 0 && segmentIds.length === 0) {
    return { audience: 0, sendable: 0, skipped: 0 };
  }

  const campaignId = input.campaignId?.trim();
  if (campaignId) {
    await setCampaignAudienceRest(campaignId, {
      listIds,
      segmentIds,
      excludeListIds: uniqueIds(input.excludeListIds ?? []),
      excludeSegmentIds: uniqueIds(input.excludeSegmentIds ?? []),
    });
    const estimate = await getCampaignAudienceEstimate(campaignId);
    return {
      audience: estimate.netCount,
      sendable: estimate.sendableCount,
      skipped: sumSkips(estimate.skips),
      skips: estimate.skips,
    };
  }

  return fetchUniqueMembershipCounts(listIds, segmentIds);
};
